"use client";

import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
  OutlinedInput,
  ListSubheader,
  Checkbox,
  Grid,
  Autocomplete,
  TextField,
} from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import React, { useEffect, useRef, useState } from "react";
import Header from "../Header/Header";
import MultiSelectFilter from "./MultiSelect";
import NewsCard from "./NewsCard";
import "./Analytics.css";
import WorkspaceAnalytics from "./WorkSpaceAnalytics";
import { toast, ToastContainer } from "react-toastify";
import axiosInstance from "../Utils/AxoisClient";
import { useLocation } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";

/* ---------------- Chart Data ---------------- */
const lineData = [
  {
    id: "London - Los Angeles",
    color: "#2563eb",
    data: [
      { x: "Qtr1", y: 95 },
      { x: "Qtr2", y: 90 },
      { x: "Qtr3", y: 85 },
      { x: "Qtr4", y: 92 },
    ],
  },
  {
    id: "Mumbai - Los Angeles",
    color: "#14b8a6",
    data: [
      { x: "Qtr1", y: 85 },
      { x: "Qtr2", y: 88 },
      { x: "Qtr3", y: 82 },
      { x: "Qtr4", y: 86 },
    ],
  },
  {
    id: "Hamburg - Los Angeles",
    color: "#a855f7",
    data: [
      { x: "Qtr1", y: 105 },
      { x: "Qtr2", y: 110 },
      { x: "Qtr3", y: 90 },
      { x: "Qtr4", y: 104 },
    ],
  },
];

const normalize = (val) => {
  if (!val) return [];

  if (Array.isArray(val)) {
    return Array.from(
      new Set(
        val.flatMap((v) =>
          typeof v === "string" ? v.split(",").map((s) => s.trim()) : [],
        ),
      ),
    );
  }

  if (typeof val === "string") {
    return val.split(",").map((s) => s.trim());
  }

  return [];
};

export default function MarketIntelligence() {
  const [tab, setTab] = useState({
    tabIndex: 0,
    tabName: "Ocean Freight",
    tabList: [
      {
        // name: "Origin Country",
        name: "Destination Country",
        multiSelect: false,
      },
      {
        // name: "Origin Port",
        name: "Destination Port",
        multiSelect: false,
      },
      {
        name: "Origin Country",
        // name: "Destination Country",
        multiSelect: true,
      },
      {
        // name: "Destination Port",
        name: "Origin Port",
        multiSelect: true,
      },
    ],
  });

  const currentTab = {
    0: "Ocean Freight",
    1: "Tariff",
    2: "Fuels",
  };

  const tabUpdatedValue = {
    0: {
      tabIndex: 0,
      tabName: "Ocean Freight",
      tabList: [
        {
          name: "Destination Country",
          multiSelect: false,
        },
        {
          name: "Destination Port",
          multiSelect: false,
        },
        {
          name: "Origin Country",
          multiSelect: true,
        },
        {
          name: "Origin Port",
          multiSelect: true,
        },
      ],
    },
    1: {
      tabIndex: 1,
      tabName: "Tariff",
      tabList: [
        {
          name: "Category",
          multiSelect: false,
        },
        {
          name: "HS Code",
          multiSelect: false,
        },
        // {
        //   name: "Origin Country",
        //   multiSelect: true,
        // },
        {
          name: "Destination Country",
          multiSelect: false,
        },
        {
          name: "Origin Country",
          multiSelect: true,
        },
      ],
    },
    2: {
      tabIndex: 2,
      tabName: "Fuel",
      tabList: [
        {
          name: "Origin Country",
          multiSelect: false,
        },
        {
          name: "Origin Port",
          multiSelect: false,
        },
        {
          name: "Destination Country",
          multiSelect: true,
        },
        {
          name: "Destination Port",
          multiSelect: true,
        },
      ],
    },
  };

  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // --- Check if user came from dashboard with location params ---
  const cameFromDashboard = Boolean(location?.state?.params);
  const skipNextClearRef = useRef(cameFromDashboard);
  const preventAutoSelectRef = useRef(false);
  // Bumped on every tab switch. Auto-select effects capture the generation
  // active when their data arrives and refuse to fire if it's stale — a
  // fixed setTimeout can't be trusted here since debounced fetches (500ms)
  // can resolve after a much shorter "prevent auto-select" window closes,
  // auto-filling a dropdown with the previous tab's data.
  const tabGenerationRef = useRef(0);

  const [thirdDropDown, setThirdDropDown] = useState(
    normalize(location?.state?.params?.origin_country),
  );
  const [secondDropDown, setSecondDropDown] = useState([
    location?.state?.params?.destination_port,
  ]);
  const [firstDropDown, setFirstDropDown] = useState([
    location?.state?.params?.destination_country,
  ]);
  const [firstDropDownData, setFirstDropDownData] = useState([]);
  const [secondDropDownData, setSecondDropDownData] = useState([]);
  const [forthDropDown, setForthDropDown] = useState(
    normalize(location?.state?.params?.origin_port),
  );
  
  const [thirdDropDownData, setThirdDropDownData] = useState([]);
  const [forthDropDownData, setForthDropDownData] = useState([]);
  const [oceanFreightData, setOceanFreightData] = useState([]);
  const [countryPortData, setCountryPortData] = useState(
    location?.state?.params?.origin_country_port
      ? [location.state.params.origin_country_port]
      : [],
  );
  const [selected, setSelected] = useState([]);
  const [fuel, setFuel] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState([]);
  const [selectedFuel, setSelectedFuel] = useState("");
  const [data, setData] = useState([]);
  const [range, setRange] = useState("1M");
  const [selectedTypes, setSelectedTypes] = useState([]);
  // Loading states for dropdowns
  const [firstLoading, setFirstLoading] = useState(false);
  const [secondLoading, setSecondLoading] = useState(false);
  const [thirdLoading, setThirdLoading] = useState(false);
  const [forthLoading, setForthLoading] = useState(false);
  // AbortController refs
  const abortControllerRef = useRef(null);
  const hsCodeAbortControllerRef = useRef(null);

  const [chartMessage, setChartMessage] = useState(
    "Please Select Required Value",
  );
  const [newsList, setNewList] = useState([
    {
      id: 1,
      image:
        "https://www.shutterstock.com/image-photo/usa-flag-memorial-day-veterans-260nw-2409028059.jpg",

      date: "Sep 11, 2024",
      title: "The US will continue to negotiate with China",
    },
  ]);
  let secondDropdownAbort = null;

  const debounceRef = useRef(null);
  const debouncePortRef = useRef(null);
  const debounceOceanRef = useRef(null);
  const secondDropdownAbortRef = useRef(null);

  const updateTab = (e, v) => {
    // Invalidate any auto-select still waiting on data from the tab we're
    // leaving — see tabGenerationRef above.
    tabGenerationRef.current += 1;
    // Cancel all pending API requests when switching tabs
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (hsCodeAbortControllerRef.current) {
      hsCodeAbortControllerRef.current.abort();
    }
    if (secondDropdownAbortRef.current) {
      secondDropdownAbortRef.current.abort();
    }
    // Clear debounce timers
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (debouncePortRef.current) {
      clearTimeout(debouncePortRef.current);
    }
    if (debounceOceanRef.current) {
      clearTimeout(debounceOceanRef.current);
    }
    // Reset loading states
    setFirstLoading(false);
    setSecondLoading(false);
    setThirdLoading(false);
    setForthLoading(false);

    setTab(tabUpdatedValue[v]);

    // --- When user came from dashboard, restore location default values on tab switch ---
    if (cameFromDashboard) {
      if (v !== 2) skipNextClearRef.current = true;
      if (v === 0) {
        // Ocean Freight: destination_country, destination_port, origin_country, origin_port
        setFirstDropDown([location?.state?.params?.destination_country]);
        setSecondDropDown([location?.state?.params?.destination_port]);
        setThirdDropDown(normalize(location?.state?.params?.origin_country));
        setForthDropDown(normalize(location?.state?.params?.origin_port));
      } else if (v === 1) {
        // Tariff: subcategory, hscode, destination_country, origin_country
        setFirstDropDown(
          {
            subcategory_name: location?.state?.params?.subcategory_name,
            id: location?.state?.params?.subcategory_id,
          } || null,
        );
        setSecondDropDown(
          { hsncode: location?.state?.params?.hsn_code } || null,
        );
        setThirdDropDown(location?.state?.params?.destination_country || null);
        setForthDropDown(normalize(location?.state?.params?.origin_country));
      } else if (v === 2) {
        // Fuel: no values from location
        setFirstDropDown([]);
        setSecondDropDown([]);
        setThirdDropDown([]);
        setForthDropDown([]);
        setCountryPortData([]);
      }
    } else {
      // --- Normal flow: clear all dropdowns when switching tabs ---
      setFirstDropDown([]);
      setSecondDropDown([]);
      setThirdDropDown([]);
      setForthDropDown([]);
      setCountryPortData([]);
      // Prevent auto-select from filling dropdowns immediately after clearing
      preventAutoSelectRef.current = true;
      setTimeout(() => {
        preventAutoSelectRef.current = false;
      }, 100);
    }
    setOceanFreightData([]);
  };


  const fetchDataSubCategory = async (value) => {
    // Abort previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new controller
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const payload = { name: value || "" };
    setFirstLoading(true);
    try {
      const response = await axiosInstance.post(
        `company/subcategory-list?limit=500&page=1`,
        payload,
        {
          signal: controller.signal, // 👈 attach signal
        },
      );

      setFirstDropDownData(response?.data?.data || []);
    } catch (error) {
      // Ignore aborted requests
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }

      toast.error("Failed to fetch data", { autoClose: 2000 });
    } finally {
      setFirstLoading(false);
    }
  };

  const fetchHScode = async (selectedSubCategory) => {
    // Abort previous request if exists
    if (hsCodeAbortControllerRef.current) {
      hsCodeAbortControllerRef.current.abort();
    }

    // Create new controller
    const controller = new AbortController();
    hsCodeAbortControllerRef.current = controller;

    const payload = {
      subcategory_id: selectedSubCategory?.id || "",
    };
    
    try {
      const response = await axiosInstance.post(
        "company/hsncode-list?limit=400&page=1",
        payload,
        {
          signal: controller.signal, // 👈 attach signal for cancellation
        },
      );

      const data = response?.data?.data;
      setSecondDropDownData(data);
    } catch (error) {
      // Ignore aborted requests
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      console.error(error);
    }
  };

  async function fetchOriginCountryAPI(payload) {
    const response = await axiosInstance.post(
      "company/analytics/origin-country",
      payload,
    );

    return response?.data?.data || [];
  }
  async function fetchOriginCountryPort(payload) {
    const response = await axiosInstance.post(
      "company/analytics/origin-port",
      payload,
    );

    return response?.data?.data || [];
  }

  const isInitialThirdDropDown = React.useRef(true);

  useEffect(() => {
    if (
      thirdDropDown === null ||
      thirdDropDown.length < 1 ||
      tab.tabName !== "Ocean Freight"
    )
      return;
    const payload = {
      origin_country: thirdDropDown?.join(",") || "",
    };
    const requestGeneration = tabGenerationRef.current;
    const fetchData = async () => {
      // old: setForthDropDown([]) was called on every run including initial load
      if (!isInitialThirdDropDown.current && !skipNextClearRef.current) {
        setForthDropDown([]);
        setCountryPortData([]);
      }
      isInitialThirdDropDown.current = false;
      setForthLoading(true);
      try {
        const res = await fetchOriginCountryPort(payload);
        if (requestGeneration !== tabGenerationRef.current) return;
        const data = res || [];
        const mappedData = data.map((item) => item?.origin_port);
        setForthDropDownData(data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }
        console.error(error);
      } finally {
        setForthLoading(false);
      }
    };
    fetchData();
  }, [thirdDropDown]);

  function mapOriginCountryData(data) {
    return data.map((item) => item.origin_country);
  }

  async function GetFirstDropDownData() {
    const isOceanFreight = tab?.tabName === "Ocean Freight";
    if (!isOceanFreight) {
      fetchDataSubCategory(); // optional reset
      return;
    }
    const requestGeneration = tabGenerationRef.current;
    const payload = {
      type: "Ocean Freight",
      origin_country: "",
      subcategory_id: "",
      hsncode: "",
    };
    setFirstLoading(true);
    try {
      //   const rawData = await fetchOriginCountryAPI(payload);
      const rawData = await getDestination(payload);
      // Drop the response if the user switched tabs while this (debounced,
      // un-abortable) request was in flight — otherwise it can auto-select
      // a value that belongs to the tab the user already left.
      if (requestGeneration !== tabGenerationRef.current) return;
      const data = rawData?.map((element) => element?.destination_country);
      const mappedData = mapOriginCountryData(rawData);
      setFirstDropDownData(data);
    } catch (error) {
      toast.error("Failed to fetch data", { autoClose: 2000 });
    } finally {
      setFirstLoading(false);
    }
  }

  async function GetSecondDropDownData(originCountries) {
    const currentTab = tab?.tabName === "Ocean Freight";
    if (!currentTab) {
      // Tariff tab: fetch HS codes. Clear the previously selected HS Code
      // synchronously, right here, rather than relying on a separate
      // effect elsewhere to notice the Category change and clear it —
      // that indirection is what was letting the old HS Code (and its
      // label) linger on screen after Category changed. Skipped on the
      // dashboard-restore pass so a restored HS Code isn't wiped out the
      // instant it's set.
      if (!skipNextClearRef.current) {
        setSecondDropDown(null);
        setSecondDropDownData([]);
        setThirdDropDown(null);
        setThirdDropDownData([]);
        setForthDropDown(tab?.tabList[3]?.multiSelect ? [] : null);
        setForthDropDownData([]);
      }
      setSecondLoading(true);
      try {
        await fetchHScode(firstDropDown ?? 0);
      } finally {
        setSecondLoading(false);
      }
      return;
    }
    if (originCountries?.length < 1) return;

    if (secondDropdownAbort) {
      secondDropdownAbort.abort();
    }
    secondDropdownAbort = new AbortController();
    const requestGeneration = tabGenerationRef.current;
    setSecondLoading(true);
    const fetchData = async () => {
      try {
        const data = await getDestinationPort();
        if (requestGeneration !== tabGenerationRef.current) return;
        const portList = data.map((ele) => ele.destination_port);
        setSecondDropDownData(portList);
      } finally {
        setSecondLoading(false);
      }
    };
    fetchData();
  }

  useEffect(() => {
    GetFirstDropDownData();
  }, [tab]);

  useEffect(() => {
    GetSecondDropDownData(firstDropDown);
  }, [firstDropDown]);

  async function getDestination(payload) {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    return new Promise((resolve) => {
      debounceRef.current = setTimeout(async () => {
        try {
          const response = await axiosInstance.post(
            "company/analytics/destination-country",
            payload,
          );

          const data = response?.data?.data ?? [];
          resolve(data);
        } catch (error) {
          if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
            resolve([]);
            return;
          }

          toast.error("Failed to fetch data", { autoClose: 2000 });
          resolve([]);
        }
      }, 500);
    });
  }

  async function getDestinationPort() {
    if (debouncePortRef.current) {
      clearTimeout(debouncePortRef.current);
    }

    return new Promise((resolve) => {
      debouncePortRef.current = setTimeout(async () => {
        const payload = {
          destination_country: firstDropDown,
        };
        try {
          const response = await axiosInstance.post(
            "company/analytics/destination-port",
            payload,
          );
          const data = response?.data?.data ?? [];
          resolve(data);
        } catch (error) {
          if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
            resolve([]);
            return;
          }

          toast.error("Failed to fetch data", { autoClose: 2000 });
          resolve([]); // ✅ always resolve
        }
      }, 500);
    });
  }

  async function getThirdDropDownData() {
    const requestGeneration = tabGenerationRef.current;
    if (tab.tabName === "Tariff") {
      if (!firstDropDown?.id || !secondDropDown?.hsncode) return;
      let payload1 = {
        type: "Tariff Progress",
        subcategory_id: firstDropDown?.id,
        hsncode: secondDropDown?.hsncode,
      };
      let payload = {
        type: "Tariff Progress",
        origin_country: "",
        subcategory_id: firstDropDown?.id,
        hsncode: secondDropDown?.hsncode,
      };
      //   const res = await fetchOriginCountryAPI(payload);
      //   const mappedData = mapOriginCountryData(res);
      //   setThirdDropDownData(mappedData);
      setThirdLoading(true);
      try {
        const res = await getDestination(payload);
        if (requestGeneration !== tabGenerationRef.current) return;
        const data = res?.map((element) => element?.destination_country);
        //   setForthDropDownData(data);

        setThirdDropDownData(data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }
        console.error(error);
      } finally {
        setThirdLoading(false);
      }
    } else {
      let payload = {
        type: "Ocean Freight", // tariff ,fuels,ocean
        subcategory_id: "", // required for tariff
        hsncode: "", // required for tariff
        destination_country: firstDropDown,
      };
      if (tab.tabName === "Fuel") return;
      setThirdLoading(true);
      try {
        const res = await fetchOriginCountryAPI(payload);
        if (requestGeneration !== tabGenerationRef.current) return;
        const data = res?.map((element) => element?.origin_country);
        setThirdDropDownData(data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }
        console.error(error);
      } finally {
        setThirdLoading(false);
      }
    }
  }

  useEffect(() => {
    getThirdDropDownData();
  }, [firstDropDown, secondDropDown]);

  const prevFirstDropDownRef = useRef(null);

  useEffect(() => {
    if (prevFirstDropDownRef.current === null) {
      prevFirstDropDownRef.current = firstDropDown;
      return;
    }

    // Skip cascading reset when coming from dashboard (Ocean Freight / Tariff)
    if (skipNextClearRef.current) {
      prevFirstDropDownRef.current = firstDropDown;
      return;
    }

    // ✅ Run only if value actually changed
    if (prevFirstDropDownRef.current !== firstDropDown) {
      setForthDropDownData([]);
      setSecondDropDownData([]);
      setThirdDropDownData([]);
      // Reset to the type each field actually expects — [] for a
      // multi-select field, null for a single-select Autocomplete (which
      // otherwise gets handed a truthy [] as its value and can render
      // stale text left over from the previous selection).
      setSecondDropDown(tab?.tabList[1]?.multiSelect ? [] : null);
      setThirdDropDown(tab?.tabList[2]?.multiSelect ? [] : null);
      setForthDropDown(tab?.tabList[3]?.multiSelect ? [] : null);
      setCountryPortData([]);
      setOceanFreightData([]);
    }
    // 🔄 Update previous value
    prevFirstDropDownRef.current = firstDropDown;
  }, [firstDropDown]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getDestinationPort();
      const portList = data.map((ele) => ele.destination_port);
      setForthDropDownData(portList);
    };
    const requestGeneration = tabGenerationRef.current;
    const getData = async (payload) => {
      setForthLoading(true);
      try {
        const res = await fetchOriginCountryAPI(payload);
        if (requestGeneration !== tabGenerationRef.current) return;
        const mappedData = mapOriginCountryData(res);
        if (!skipNextClearRef.current) {
          setForthDropDown([]);
          setCountryPortData([]);
        }
        skipNextClearRef.current = false;
        setForthDropDownData(mappedData);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }
        console.error(error);
      } finally {
        setForthLoading(false);
      }
      //   const res = await getDestination(payload);
      //   const data = res?.map((element) => element?.destination_country);
      //   setForthDropDownData(data);
    };
    if (tab.tabName === "Tariff") {
      if (!firstDropDown?.id || !secondDropDown?.hsncode) return;
      let payload = {
        type: "Tariff Progress",
        // // origin_country: thirdDropDown?.join(","),
        subcategory_id: firstDropDown?.id,
        hsncode: secondDropDown?.hsncode,
        destination_country: thirdDropDown,
        // destination_port: secondDropDown,
      };
      getData(payload);
    } else {
      //   fetchData();
    }
  }, [firstDropDown, secondDropDown, thirdDropDown]);

  async function getOceanFreight() {
    if (debounceOceanRef.current) {
      clearTimeout(debounceOceanRef.current);
    }
    return new Promise((resolve) => {
      debounceOceanRef.current = setTimeout(async () => {
        let payload = {};
        if (tab?.tabName === "Tariff") {
          //   const countryList = thirdDropDown?.map((ele) => {
          //     return { origin_country: ele };
          //   });
          const countryList = forthDropDown?.map((ele) => {
            return { origin_country: ele };
          });
          payload = {
            subcategory_id: firstDropDown?.id,
            hsncode: secondDropDown?.hsncode,
            origin_country_port_array: countryList,
            // destination_country: forthDropDown,
            destination_country: thirdDropDown,
          };
        } else {
          const normalize = (arr) =>
            (Array.isArray(arr[0]) ? arr[0] : arr).filter(Boolean);
          payload = {
            origin_country_port_array: normalize(countryPortData),
            destination_country: firstDropDown,
            destination_port: secondDropDown,
          };
        }
        let url = "";
        if (tab?.tabName === "Tariff") {
          url = "company/analytics/tariff-data";
        } else {
          url = "company/analytics/ocean-freight-data";
        }

        try {
          const response = await axiosInstance.post(url, payload);
          const data = response?.data?.data ?? [];
          resolve(data);
        } catch (error) {
          if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
            resolve([]);
            return;
          }
          toast.error("Failed to fetch data", { autoClose: 2000 });
          resolve([]); // ✅ always resolve
        }
      }, 500);
    });
  }

  useEffect(() => {
    const fetchData = async () => {
      if (forthDropDown?.length < 1) return;
      const data = await getOceanFreight();
      const oceanFreightData = data?.map((ele) => {
        // return { ...ele, id: ele?.name };

        return {
          ...ele,
          id:
            tab.tabName === "Ocean Freight"
              ? `${ele?.raw?.origin_port}-${ele?.raw?.destination_port}`
              : `${ele?.raw?.origin_country_name}-${ele?.raw?.destination_country_name}`,
        };
      });
      setOceanFreightData(oceanFreightData);
      if (oceanFreightData.length < 1) {
        setChartMessage("No Data Found");
      }
    };

    fetchData();
  }, [forthDropDown, tab]);

  const hasSinglePoint = oceanFreightData?.some(
    (serie) => serie.data.length === 1,
  );

  const convertToRechartsData = (nivoData) => {
    if (!nivoData || nivoData.length === 0) return [];
    const quarterMap = {};
    nivoData.forEach((series) => {
      series.data?.forEach((point) => {
        if (!quarterMap[point.x]) {
          quarterMap[point.x] = { quarter: point.x };
        }
        quarterMap[point.x][series.id] = point.y;
      });
    });
    return Object.values(quarterMap);
  };

  const rechartsData = convertToRechartsData(oceanFreightData);
  const seriesKeys = oceanFreightData?.map((series) => series.id) || [];
  const isTariffTab = tab?.tabName === "Tariff";
  const ChartComponent = isTariffTab ? BarChart : LineChart;
  // Extract colors from data with fallback colors
  const defaultColors = [
    "#592DFF",
    "#2400A0",
    "#9F3FD1",
    "#5C0FA8",
    "#7C5CFF",
    "#B0179C",
    "#3D14D6",
    "#D946EF",
  ];

  const seriesColors =
    oceanFreightData?.reduce((acc, series, index) => {
      acc[series.id] = defaultColors[index % defaultColors.length];
      return acc;
    }, {}) || {};

  const handleChange = (event) => {
    const values = event.target.value; // ["Houston", "Singapore"]
    setSelected(values);

    const result = locationData.flatMap((group) =>
      group.locations
        .filter((loc) => values.includes(loc))
        .map((loc) => ({
          region: group.region,
          location: loc,
        })),
    );

    setSelectedLocationDetails(result);
  };

  const handleGroupToggle = (group) => {
    const allLocations = group.locations || [];
    const allSelected = allLocations.every((loc) => selected.includes(loc));

    let newSelected;
    if (allSelected) {
      newSelected = selected.filter((loc) => !allLocations.includes(loc));
    } else {
      const toAdd = allLocations.filter((loc) => !selected.includes(loc));
      newSelected = [...selected, ...toAdd];
    }

    setSelected(newSelected);

    const result = locationData.flatMap((g) =>
      g.locations
        .filter((loc) => newSelected.includes(loc))
        .map((loc) => ({
          region: g.region,
          location: loc,
        })),
    );
    setSelectedLocationDetails(result);
  };

  async function getListOfFuel() {
    const response = await axiosInstance.post(
      "company/analytics/fuels-listing",
      {
        fuel_type: selectedTypes, //Fuel or Brent
      },
    );
    const data = response?.data?.data ?? [];
    setFuel(data);
  }
  useEffect(() => {
    if (tab?.tabIndex !== 2 || selectedTypes === "") return;
    getListOfFuel();
  }, [tab, selectedTypes]);

  async function getLocation(fuel) {
    const response = await axiosInstance.post(
      "company/analytics/region-listing",
      { fuel_name: fuel, fuel_type: selectedTypes },
    );
    const data = response?.data?.data ?? [];
    // A location can come back blank when it represents the whole region
    // rather than a specific city/sub-location — fall back to the region
    // name so it still renders with a visible label and stays selectable
    // (instead of an unlabeled, unclickable checkbox).
    const sanitized = data.map((group) => {
      const mapped = (group?.locations || []).map((loc) =>
        typeof loc === "string" && loc.trim() !== "" ? loc : group?.region,
      );
      return { ...group, locations: Array.from(new Set(mapped)) };
    });
    setLocationData(sanitized);
  }
  useEffect(() => {
    if (selectedFuel === "" || selected === null || selectedTypes === "")
      return;
    getLocation(selectedFuel);
  }, [selectedFuel]);

  const buildChartData = (data) => {
    return data.map((item) => ({
      label: item.label,
      price: item.price?.toFixed(2),
    }));
  };
  async function fuelChartDetails() {
    const payload = {
      fuel_name: selectedFuel,
      fuel_type: selectedTypes, //Fuel or Brent
      pairs: selectedLocationDetails,
      range: range, // 1M or 3M or 6M or 1Y or ALL
    };
    const response = await axiosInstance.post(
      "company/analytics/fuel-price/chart",
      payload,
    );

    const data = response?.data?.data ?? [];
    // const chartData = data.fuel.map((fuelItem) => {
    //   const brentItem = data.brent.find((b) => b.label === fuelItem.label);

    //   return {
    //     quarter: fuelItem.label,
    //     Brent: brentItem?.price || 0,
    //     Fuel: fuelItem.price,
    //   };
    // });

    // const chartData = buildChartData(data.fuel, data.brent);
    const chartData = buildChartData(data);

    setData(chartData);

    // setData(chartData);
  }

  useEffect(() => {
    if (range && selectedFuel && selectedLocationDetails?.length) {
      fuelChartDetails();
    }
  }, [selectedLocationDetails, selectedFuel, range]);

  const seriesConfig = [
    ...selectedLocationDetails.map((item, index) => ({
      key: `Fuel_${item.location}`,
      axis: "left",
      color: ["#4dabf7", "#1c7ed6", "#74c0fc"][index % 3],
      dashed: false,
    })),
    ...selectedLocationDetails.map((item, index) => ({
      key: `Brent_${item.location}`,
      axis: "right",
      color: ["#ff6b6b", "#e03131", "#ff8787"][index % 3],
      dashed: true,
    })),
  ];

  const handleChangeType = (event) => {
    setSelectedTypes(event.target.value);
    setSelectedFuel("");
    setSelected([]);
    setSelectedLocationDetails([]);
    setData([]);
  };

  /* ========== AUTO-SELECT: set first value as default when data loads ========== */
  // Helper: checks if a dropdown value is empty/unset
  const isDropdownEmpty = (val) => {
    if (!val) return true;
    if (Array.isArray(val)) return val.filter(Boolean).length === 0;
    if (typeof val === "string") return val.trim() === "";
    return false;
  };

  // Auto-select first dropdown (Destination Country / Category)
  // When data loads and no value is selected (no bookmark or user cleared), pick first item
  useEffect(() => {
    if (skipNextClearRef.current || preventAutoSelectRef.current) return;
    if (firstDropDownData?.length > 0 && isDropdownEmpty(firstDropDown)) {
      setFirstDropDown(firstDropDownData[0]);
    }
  }, [firstDropDownData]);

  // Auto-select second dropdown (Destination Port / HS Code)
  useEffect(() => {
    if (skipNextClearRef.current || preventAutoSelectRef.current) return;
    if (secondDropDownData?.length > 0 && isDropdownEmpty(secondDropDown)) {
      setSecondDropDown(secondDropDownData[0]);
    }
  }, [secondDropDownData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select third dropdown (Origin Country / Destination Country)
  useEffect(() => {
    if (skipNextClearRef.current || preventAutoSelectRef.current) return;
    if (thirdDropDownData?.length > 0 && isDropdownEmpty(thirdDropDown)) {
      // Multi-select needs array, single-select needs plain value
      if (tab?.tabList[2]?.multiSelect) {
        setThirdDropDown([thirdDropDownData[0]]);
      } else {
        setThirdDropDown(thirdDropDownData[0]);
      }
    }
  }, [thirdDropDownData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select fourth dropdown (Origin Port / Origin Country)
  useEffect(() => {
    if (skipNextClearRef.current || preventAutoSelectRef.current) return;
    if (forthDropDownData?.length > 0 && isDropdownEmpty(forthDropDown)) {
      const firstItem = forthDropDownData[0];
      if (tab?.tabList[3]?.multiSelect) {
        // Extract value: object (Ocean Freight) vs string (Tariff)
        const value =
          typeof firstItem === "object"
            ? (firstItem?.origin_port ?? "")
            : firstItem;
        setForthDropDown([value]);
        // Also set countryPortData for Ocean Freight chart payload
        if (tab?.tabName === "Ocean Freight" && typeof firstItem === "object") {
          setCountryPortData([
            {
              origin_country: firstItem.origin_country,
              origin_port: firstItem.origin_port,
            },
          ]);
        }
      } else {
        setForthDropDown(firstItem);
      }
    }
  }, [forthDropDownData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select Type dropdown (Fuel tab) — default to "Fuel"
  useEffect(() => {
    // if (preventAutoSelectRef.current) return;
    if (tab?.tabIndex === 2 && isDropdownEmpty(selectedTypes)) {
      setSelectedTypes("Fuel");
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select Fuel Name dropdown when fuel list loads
  useEffect(() => {
    if (preventAutoSelectRef.current) return;
    if (fuel?.length > 0 && selectedFuel === "") {
      setSelectedFuel(fuel[0]?.fuel_name || "");
    }
  }, [fuel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first Region when location data loads
  useEffect(() => {
    if (preventAutoSelectRef.current) return;
    if (locationData?.length > 0 && selected?.length === 0) {
      const firstLocation = locationData[0]?.locations?.[0];
      if (firstLocation) {
        setSelected([firstLocation]);
        // Also set selectedLocationDetails for chart payload
        setSelectedLocationDetails([
          {
            region: locationData[0]?.region,
            location: firstLocation,
          },
        ]);
      }
    }
  }, [locationData]); // eslint-disable-line react-hooks/exhaustive-deps
  /* ========== END AUTO-SELECT ========== */

  // Stable string identities for the remount keys above — a dropdown's
  // own value can be an object, a string, or an array depending on the
  // tab, so this normalizes whatever it is into something key-safe.
  const toDropDownKey = (val) => {
    if (val == null) return "empty";
    if (Array.isArray(val)) {
      return val.length
        ? val.map((v) => toDropDownKey(v)).join(",")
        : "empty";
    }
    if (typeof val === "object") {
      return val.id ?? val.hsncode ?? val.name ?? JSON.stringify(val);
    }
    return String(val);
  };
  const firstDropDownKey = toDropDownKey(firstDropDown);
  const secondDropDownKey = toDropDownKey(secondDropDown);
  const thirdDropDownKey = toDropDownKey(thirdDropDown);

  return (
    <>
      <Header />
      <ToastContainer position="top-right" />

      <Box
        sx={{
          background: "linear-gradient(90deg,#6d4bd3,#2b144f)",
          m: 0,
          textAlign: "center",
          marginTop: "67px",
          height: 80,
        }}
      >
        <p className="header-text-style">Market Intelligence</p>
      </Box>

      {/* Header */}
      <Box
        sx={{
          minHeight: tab?.tabIndex !== 2 ? "100vh" : "auto",
          bgcolor: "#f4f6fa",
          p: { xs: 1.5, sm: 2.5, md: 3.5 },
        }}
      >
        {
          <>
            {/* Tabs */}
            <Paper
              sx={{
                mt: 0,
                bgcolor: "#2b0057",
                borderRadius: 3,
                boxShadow: "0 8px 28px rgba(44, 0, 62, 0.14)",
                overflow: "hidden",
              }}
            >
              <Tabs
                value={tab?.tabIndex}
                onChange={(e, v) => updateTab(e, v)}
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons="auto"
                textColor="inherit"
                sx={{
                  display: "flex",
                  //   width: "100vw",
                  justifyContent: "space-between",
                }}
                TabIndicatorProps={{
                  style: {
                    background: "#fff",
                  },
                }}
              >
                <Tab
                  label="Ocean Freight "
                  sx={tabStyle(tab?.tabIndex === 0)}
                />
                <Tab label="Tariff" sx={tabStyle(tab?.tabIndex === 1)} />
                <Tab label="Fuels" sx={tabStyle(tab?.tabIndex === 2)} />
              </Tabs>
            </Paper>
            {
              <>
                <Paper
                  sx={{
                    mt: 2.5,
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: 3,
                    border: "1px solid #eef0f4",
                    boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
                  }}
                >
                  <Box
                    display="grid"
                    gridTemplateColumns={{
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "repeat(4,1fr)",
                    }}
                    gap={2}
                  >
                    {tab?.tabIndex !== 2 ? (
                      <>
                        <MultiSelectFilter
                          label={tab?.tabList[0]?.name || ""}
                          isMultiple={tab?.tabList[0]?.multiSelect ?? true}
                          value={firstDropDown}
                          options={firstDropDownData ?? []}
                          onChange={(newValue) => {
                            // A manual change here is the user actively
                            // picking a new value — the "came from
                            // dashboard, skip the first reset" grace
                            // period is over the moment that happens, even
                            // if it hasn't self-cleared yet (it only does
                            // so once a full Tariff fetch cycle succeeds).
                            skipNextClearRef.current = false;
                            setFirstDropDown(newValue);
                          }}
                          loading={firstLoading}
                        />
                        <MultiSelectFilter
                          // Force a clean remount whenever the upstream
                          // (Category/Destination) selection changes,
                          // instead of relying on MUI to fully reconcile
                          // its internal input text against a new value —
                          // that's what was leaving the old HS Code text
                          // visible after the field it depends on changed.
                          key={`second-${firstDropDownKey}`}
                          value={secondDropDown}
                          label={tab?.tabList[1]?.name || ""}
                          isMultiple={tab?.tabList[1]?.multiSelect ?? true}
                          options={secondDropDownData ?? []}
                          onChange={setSecondDropDown}
                          loading={secondLoading}
                          disabled={isDropdownEmpty(firstDropDown)}
                        />
                        <MultiSelectFilter
                          key={`third-${firstDropDownKey}-${secondDropDownKey}`}
                          value={thirdDropDown}
                          label={tab?.tabList[2]?.name || ""}
                          isMultiple={tab?.tabList[2]?.multiSelect ?? true}
                          options={thirdDropDownData}
                          disabled={isDropdownEmpty(secondDropDown)}
                          onChange={setThirdDropDown}
                          loading={thirdLoading}
                        />
                        <MultiSelectFilter
                          key={`forth-${firstDropDownKey}-${secondDropDownKey}-${thirdDropDownKey}`}
                          value={forthDropDown}
                          label={tab?.tabList[3]?.name || ""}
                          isMultiple={tab?.tabList[3]?.multiSelect ?? true}
                          options={forthDropDownData}
                          onChange={setForthDropDown}
                          onMetaChange={setCountryPortData}
                          loading={forthLoading}
                          disabled={isDropdownEmpty(thirdDropDown)}
                        />
                      </>
                    ) : (
                      <>
                        <Box gridColumn="1 / -1">
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr 1fr",
                              },
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            <Box>
                              <Box className="label_name">
                                &nbsp;
                                <span className="label-style">Type</span>
                              </Box>
                              <Select
                                value={selectedTypes}
                                onChange={handleChangeType}
                                input={<OutlinedInput size="small" />}
                                displayEmpty
                                renderValue={(val) =>
                                  val && val.length > 0 ? (
                                    val
                                  ) : (
                                    <span style={{ color: "#aaa" }}>
                                      Select Type
                                    </span>
                                  )
                                }
                                sx={{
                                  height: 37,
                                  borderRadius: "10px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#e2e5ea",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline":
                                    { borderColor: "#c9cdd6" },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#592DFF",
                                      borderWidth: "1.5px",
                                    },
                                }}
                                fullWidth
                              >
                                {["Fuel", "Brent"].map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Box>
                            <Box>
                              <Box className="label_name">
                                &nbsp;
                                <span className="label-style">Fuel Name</span>
                              </Box>
                              <Autocomplete
                                size="small"
                                fullWidth
                                options={Array.isArray(fuel) ? fuel : []}
                                getOptionLabel={(option) =>
                                  option?.fuel_name || ""
                                }
                                value={
                                  Array.isArray(fuel)
                                    ? fuel.find(
                                        (f) => f.fuel_name === selectedFuel,
                                      ) || null
                                    : null
                                }
                                onChange={(e, newValue) => {
                                  setSelected([]);
                                  setSelectedLocationDetails([]);
                                  setData([]);
                                  setSelectedFuel(
                                    newValue ? newValue.fuel_name : "",
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Fuel"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        height: 37,
                                        borderRadius: "10px",
                                        "& fieldset": {
                                          borderColor: "#e2e5ea",
                                        },
                                        "&:hover fieldset": {
                                          borderColor: "#c9cdd6",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#592DFF",
                                          borderWidth: "1.5px",
                                        },
                                      },
                                    }}
                                  />
                                )}
                                isOptionEqualToValue={(option, value) =>
                                  option?.fuel_name === value?.fuel_name
                                }
                              />
                            </Box>
                            <Box>
                              <Box className="label_name">
                                &nbsp;
                                <span className="label-style">Location</span>
                              </Box>
                              <Select
                                multiple
                                value={selected}
                                onChange={handleChange}
                                input={<OutlinedInput size="small" />}
                                displayEmpty
                                fullWidth
                                renderValue={(selected) =>
                                  selected.length > 0 ? (
                                    selected.join(", ")
                                  ) : (
                                    <span style={{ color: "#aaa" }}>
                                      Select Region
                                    </span>
                                  )
                                }
                                sx={{
                                  borderRadius: "10px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#e2e5ea",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline":
                                    { borderColor: "#c9cdd6" },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#592DFF",
                                      borderWidth: "1.5px",
                                    },
                                  "& .MuiSelect-select": {
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontSize: 13,
                                    // fontWeight: 600,
                                    height: 20,
                                  },
                                }}
                              >
                                {locationData.map((group) => [
                                  <ListSubheader
                                    key={group.region}
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: 13,
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: "pointer",
                                      lineHeight: "36px",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGroupToggle(group);
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        group.locations.length > 0 &&
                                        group.locations.every((loc) =>
                                          selected.includes(loc),
                                        )
                                      }
                                      indeterminate={
                                        group.locations.some((loc) =>
                                          selected.includes(loc),
                                        ) &&
                                        !group.locations.every((loc) =>
                                          selected.includes(loc),
                                        )
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={() => handleGroupToggle(group)}
                                      sx={{ mr: 0.5, p: 0 }}
                                    />
                                    {group.region}
                                  </ListSubheader>,
                                  group.locations.map((option) => (
                                    <MenuItem
                                      key={option}
                                      value={option}
                                      sx={{ fontSize: 12, py: 0.5, pl: 4 }}
                                    >
                                      <Checkbox
                                        checked={selected.includes(option)}
                                        size="small"
                                      />
                                      {option}
                                    </MenuItem>
                                  )),
                                ])}
                              </Select>
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </Paper>
                {/* old: no alignItems stretch */}
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "1fr",
                    md: "2fr 1fr",
                  }}
                  alignItems="stretch"
                  gap={2}
                  mt={2}
                >
                  {/* Chart */}
                  {/* old: no fixed height on chart Paper */}
                  <Paper
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: 3,
                      border: "1px solid #eef0f4",
                      boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
                      height: { xs: "auto", md: "64.5vh" },
                      maxHeight: { md: "64.5vh" },
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      fontWeight={600}
                      fontSize={{ xs: 12, sm: 14 }}
                      mb={1}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      {currentTab[tab?.tabIndex]}
                    </Typography>
                    <Box
                      // old: height: { xs: 260, sm: 300, md: 350 }
                      sx={{
                        height: { xs: 260, sm: 300 },
                        flex: { md: 1 },
                        minHeight: 0,
                      }}
                    >
                      {tab?.tabIndex !== 2 ? (
                        <>
                          {/* old: ResponsiveLine from @nivo/line */}
                          {rechartsData?.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <ChartComponent
                                data={rechartsData}
                                {...(isTariffTab
                                  ? { barCategoryGap: "35%", barGap: 4 }
                                  : {})}
                                margin={{
                                  top: 5,
                                  right: isMobile ? 10 : 30,
                                  bottom: isMobile ? 50 : 20,
                                  left: isMobile ? 10 : 20,
                                }}
                              >
                                <XAxis
                                  dataKey="quarter"
                                  tick={{ fontSize: isMobile ? 10 : 12 }}
                                  tickLine={false}
                                  angle={isMobile ? -30 : 0}
                                  textAnchor={isMobile ? "end" : "middle"}
                                  label={{
                                    value: "Quarter",
                                    position: "insideBottom",
                                    offset: isMobile ? -20 : 0,
                                    style: {
                                      fontWeight: 600,
                                      fontSize: isMobile ? 11 : 12,
                                    },
                                  }}
                                />
                                <YAxis
                                  tick={{ fontSize: isMobile ? 10 : 12 }}
                                  tickLine={false}
                                  label={
                                    !isMobile
                                      ? {
                                          value:
                                            tab?.tabName === "Tariff"
                                              ? "Tariff (%/USD)"
                                              : "Ocean Freight (USD Per MT)",
                                          angle: -90,
                                          position: "insideLeft",
                                          style: {
                                            textAnchor: "middle",
                                            fontWeight: 600,
                                            fontSize: 12,
                                          },
                                        }
                                      : undefined
                                  }
                                />
                                <Tooltip
                                  contentStyle={{
                                    background: "#fff",
                                    color: "#1E1E2D",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    borderRadius: 10,
                                    border: "1px solid #eef0f4",
                                    boxShadow:
                                      "0 8px 24px rgba(15, 23, 42, 0.1)",
                                  }}
                                  wrapperStyle={{ zIndex: 1000 }}
                                  formatter={(value) =>
                                    tab?.tabName === "Tariff"
                                      ? `${value}%`
                                      : value
                                  }
                                />
                                <Legend
                                  verticalAlign="bottom"
                                  align="center"
                                  wrapperStyle={{
                                    paddingTop: 18,
                                    paddingBottom: 0,
                                    fontSize: isMobile ? 10 : 12,
                                    bottom: 0,
                                  }}
                                  iconType="circle"
                                  iconSize={10}
                                />
                                {seriesKeys.map((key, index) =>
                                  isTariffTab ? (
                                    <Bar
                                      key={key}
                                      dataKey={key}
                                      fill={
                                        seriesColors[key] ||
                                        defaultColors[
                                          index % defaultColors.length
                                        ]
                                      }
                                      radius={[4, 4, 0, 0]}
                                      barSize={isMobile ? 24 : 45}
                                    >
                                      <LabelList
                                        dataKey={key}
                                        position="insideTop"
                                        fill="#fff"
                                        fontSize={isMobile ? 10 : 12}
                                        fontWeight={600}
                                        formatter={(value) => `${value}%`}
                                      />
                                    </Bar>
                                  ) : (
                                    <Line
                                      key={key}
                                      type="monotone"
                                      dataKey={key}
                                      stroke={
                                        seriesColors[key] ||
                                        defaultColors[
                                          index % defaultColors.length
                                        ]
                                      }
                                      strokeWidth={2}
                                      dot={{
                                        r: hasSinglePoint
                                          ? 5
                                          : isMobile
                                            ? 3
                                            : 4,
                                        fill: "#fff",
                                        strokeWidth: 2,
                                      }}
                                      activeDot={{ r: 6 }}
                                    />
                                  ),
                                )}
                              </ChartComponent>
                            </ResponsiveContainer>
                          ) : (
                            <Box
                              sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                color: "#94a3b8",
                                fontSize: 14,
                                fontWeight: 500,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "50%",
                                  background: "#f7f8fc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M3 3v18h18M7 15l4-4 3 3 5-6"
                                    stroke="#c9cdd6"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </Box>
                              {chartMessage}
                            </Box>
                          )}
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              minHeight: 300,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {/* Time Range Filter */}
                            <div
                              style={{
                                display: "flex",
                                gap: 6,
                                marginBottom: 12,
                                alignSelf: "flex-end",
                              }}
                            >
                              {["1M", "3M", "6M", "1Y", "ALL"].map((r) => (
                                <button
                                  key={r}
                                  onClick={() => setRange(r)}
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    border:
                                      range === r
                                        ? "1px solid transparent"
                                        : "1px solid #e2e5ea",
                                    background:
                                      range === r
                                        ? "linear-gradient(135deg, #592DFF 0%, #2C003E 100%)"
                                        : "#fff",
                                    color: range === r ? "#fff" : "#475569",
                                    cursor: "pointer",
                                    transition:
                                      "background-color 0.15s ease, box-shadow 0.15s ease",
                                    boxShadow:
                                      range === r
                                        ? "0 2px 8px rgba(89, 45, 255, 0.35)"
                                        : "none",
                                  }}
                                >
                                  {r}
                                </button>
                              ))}
                            </div>

                            {/* Chart */}
                            {/* <ResponsiveContainer
                              width="100%"
                              height="100%"
                              // width="100%" height={350}
                            >
                              {data.length ? (
                                <LineChart data={data}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="quarter" />

                                  {[
                                    ...new Set(seriesConfig.map((s) => s.axis)),
                                  ].map((axis) => (
                                    <YAxis
                                      key={axis}
                                      yAxisId={axis}
                                      orientation={
                                        axis === "right" ? "right" : "left"
                                      }
                                    />
                                  ))}

                                  <Tooltip />
                                  <Legend />

                                  {seriesConfig.map((s) => (
                                    <Line
                                      key={s.key}
                                      yAxisId={s.axis}
                                      type="monotone"
                                      dataKey={s.key}
                                      stroke={s.color}
                                      strokeDasharray={s.dashed ? "5 5" : "0"}
                                      strokeWidth={2}
                                    />
                                  ))}
                                </LineChart>
                              ) : (
                                <Box
                                  sx={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: { xs: 12, sm: 14 },
                                    color: "#666",
                                  }}
                                >
                                  No Record found
                                </Box>
                              )}
                            </ResponsiveContainer> */}

                            {/* <ResponsiveContainer width="100%" height="100%">
                              {data.length ? (
                                <LineChart data={data}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis
                                    dataKey="quarter"
                                    label={{
                                      value: "Time period",
                                      position: "insideBottom",
                                      offset: -10,
                                      style: { fontWeight: 600 },
                                    }}
                                  />

                                  {[
                                    ...new Set(seriesConfig.map((s) => s.axis)),
                                  ].map((axis) => (
                                    <YAxis
                                      key={axis}
                                      yAxisId={axis}
                                      label={{
                                        value:
                                          axis === "right"
                                            ? "Brent (USD/BBL) "
                                            : "Fuel (USD/MT) ", // dynamic text
                                        angle: axis !== "right" ? -90 : -270,
                                        position:
                                          axis === "right"
                                            ? "insideRight"
                                            : "insideLeft",
                                        style: {
                                          textAnchor: "middle",
                                          fontWeight: 600,
                                        },
                                      }}
                                      orientation={
                                        axis === "right" ? "right" : "left"
                                      }
                                    />
                                  ))}

                                  <Tooltip />
                                  <Legend
                                    // verticalAlign="bottom"
                                    // align="right"
                                    wrapperStyle={{
                                      paddingTop: 10,
                                      // fontSize: 14,
                                    }}
                                  />

                                  {seriesConfig.map((s) => (
                                    <Line
                                      key={s.key}
                                      yAxisId={s.axis}
                                      type="monotone"
                                      dataKey={s.key}
                                      stroke={s.color}
                                      strokeDasharray={s.dashed ? "5 5" : "0"}
                                      strokeWidth={2}
                                    />
                                  ))}
                                </LineChart>
                              ) : (
                                <Box
                                  sx={{
                                    width: "100vh",
                                    height: "50vh",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: { xs: 12, sm: 14 },
                                    color: "#666",
                                  }}
                                >
                                  No Record found
                                </Box>
                              )}
                            </ResponsiveContainer> */}
                            {data.length ? (
                              <ResponsiveContainer
                                width="100%"
                                height={isMobile ? 240 : 300}
                              >
                                <LineChart
                                  data={data}
                                  margin={{
                                    top: 5,
                                    right: isMobile ? 10 : 20,
                                    bottom: isMobile ? 40 : 10,
                                    left: isMobile ? 10 : 20,
                                  }}
                                >
                                  <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: isMobile ? 9 : 12 }}
                                    interval="preserveStartEnd"
                                    angle={isMobile ? -40 : 0}
                                    textAnchor={isMobile ? "end" : "middle"}
                                    dy={isMobile ? 6 : 0}
                                  />
                                  <YAxis
                                    tick={{ fontSize: isMobile ? 9 : 12 }}
                                    width={isMobile ? 50 : 80}
                                    label={{
                                      value:
                                        selectedTypes === "Brent"
                                          ? "Brent (USD/BBL)"
                                          : "Fuel (USD/MT)",
                                      angle: -90,
                                      position: "insideLeft",
                                      style: {
                                        textAnchor: "middle",
                                        fontWeight: 600,
                                        fontSize: isMobile ? 10 : 12,
                                      },
                                    }}
                                  />
                                  <Tooltip
                                    formatter={(v) =>
                                      `${v} ${selectedTypes === "Brent" ? "USD/BBL" : "USD/MT"}`
                                    }
                                  />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="price"
                                    name="Price"
                                    stroke="#1976d2"
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    activeDot={{ r: 5 }}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            ) : (
                              <Box
                                sx={{
                                  height: 300,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                  color: "#94a3b8",
                                  fontSize: 14,
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    background: "#f7f8fc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M3 3v18h18M7 15l4-4 3 3 5-6"
                                      stroke="#c9cdd6"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </Box>
                                {selectedFuel && selected.length > 0
                                  ? "No Data Found"
                                  : "Please Select Required Value"}
                              </Box>
                            )}
                          </div>
                        </>
                      )}
                    </Box>
                    {/* Mobile legend for Ocean Freight / Tariff */}
                    {isMobile &&
                      oceanFreightData?.length > 0 &&
                      tab?.tabIndex !== 2 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px 10px",
                            mt: 1.5,
                            px: 0.5,
                          }}
                        >
                          {oceanFreightData.map((series) => (
                            <Box
                              key={series.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  backgroundColor: series.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography sx={{ fontSize: 10, color: "#333" }}>
                                {series.id}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                  </Paper>
                  {/* News */}
                  <Paper
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      border: "1px solid #eef0f4",
                      boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
                      height: { xs: "auto", md: "64.5vh" },
                      maxHeight: { md: "64.5vh", sm: "64.5vh", xm:'64.5vh' },
                      overflow: "auto",
                    }}
                  >
                    {newsList.map((item) => (
                      <NewsCard
                        key={item.id}
                        image={item.image}
                        date={item.date}
                        title={item.title}
                        tabName={tab?.tabName}
                        originCountries={
                          tab?.tabIndex == 0
                            ? thirdDropDown
                            : tab?.tabIndex == 1
                              ? forthDropDown
                              : selectedLocationDetails
                        }
                        destinationCountry={
                          tab?.tabIndex == 0
                            ? firstDropDown
                            : tab?.tabIndex == 1
                              ? thirdDropDown
                              : selectedLocationDetails
                        }
                      />
                    ))}
                  </Paper>
                </Box>
              </>
            }
          </>
        }
      </Box>

      {/* Workspace Analytics */}
      <Box
        sx={{
          background: "linear-gradient(90deg,#6d4bd3,#2b144f)",
          m: 0,
          textAlign: "center",
          // marginTop: "67px",
          height: 80,
        }}
      >
        <p className="header-text-style">Workspace Analytics</p>
      </Box>
      {/* Workspace Analytics */}
      <Box className="market-analytics-body">
        <WorkspaceAnalytics />
      </Box>
      {/* ocean fuel and tariff */}
    </>
  );
}

const tabStyle = (active) => ({
  color: active ? "#2b0057" : "rgba(255,255,255,0.85)",
  bgcolor: active ? "#fff" : "transparent",
  borderRadius: 2,
  m: 1,
  textTransform: "none",
  fontWeight: active ? 700 : 500,
  flex: 1,
  textAlign: "center",
  boxShadow: active ? "0 4px 14px rgba(0,0,0,0.15)" : "none",
  transition: "background-color 0.2s ease, color 0.2s ease",
  "&:hover": {
    bgcolor: active ? "#fff" : "rgba(255,255,255,0.1)",
  },
});
